import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { CardGroup, CardReview, ReviewItem, StatementDetail, StatementGroup, TripGroup, TripInfo } from '../../../services/src/shared/api-types.ts';
import type { ScreenProps } from '../App.tsx';
import type { Api } from '../api.ts';
import { day, money, ordinal, person, plural, RULE_LABELS, when } from '../format.ts';
import { Empty, ErrorBanner, Loading } from '../ui.tsx';

const sum = (xs: { amount: number }[]) => xs.reduce((s, x) => s + x.amount, 0);

/** "22 charges", "1 check", "3 lines". */
function lineCount(item: ReviewItem): string {
  const n = item.lines.length;
  if (item.rule === 'held-check') return plural(n, 'check');
  return item.lines.every((l) => l.amount > 0) ? plural(n, 'charge') : plural(n, 'line');
}

/** #/review/statement/<accountId>/<closesOn> opens one statement. */
function statementFromHash(): { accountId: string; closesOn: string } | null {
  const m = location.hash.match(/^#\/?review\/statement\/([^/]+)\/(\d{4}-\d{2}-\d{2})$/);
  return m ? { accountId: decodeURIComponent(m[1]), closesOn: m[2] } : null;
}

export function Review(props: ScreenProps) {
  const [statement, setStatement] = useState(statementFromHash);
  useEffect(() => {
    const onHash = () => setStatement(statementFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return statement ? <StatementView api={props.api} {...statement} /> : <ReviewQueue {...props} />;
}

const itemsOf = (s: StatementGroup) => s.trips.flatMap((t) => t.items);
const waitingOn = (c: CardGroup) => c.statements.reduce((n, s) => n + itemsOf(s).length, 0);

function ReviewQueue({ api, refreshSummary, isAdmin }: ScreenProps) {
  const [data, setData] = useState<CardReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rule, setRule] = useState('');
  const [announce, setAnnounce] = useState('');
  const [recent, setRecent] = useState<ReviewItem[]>([]);

  const load = useCallback(() => {
    setError(null);
    api.reviewCards().then(setData, (e: Error) => setError(e.message));
  }, [api]);
  useEffect(load, [load]);

  // Matches a person decided, newest first. Posted ones keep who approved them; rule-confirmed ones have no decider.
  const loadRecent = useCallback(() => {
    Promise.all((['approved', 'posted', 'rejected'] as const).map((s) => api.review(s).then((r) => r.items, () => [])))
      .then((lists) => setRecent(byDecision(lists.flat())))
      .catch(() => setRecent([]));
  }, [api]);
  useEffect(loadRecent, [loadRecent]);

  const decided = (updated: ReviewItem) => {
    // Approving one held-check alternative closes the others.
    const gone = (i: ReviewItem) => i.id === updated.id || (updated.status === 'approved' && Boolean(updated.group) && i.group === updated.group);
    setData((prev) =>
      prev && {
        ...prev,
        cards: prev.cards.map((c) => ({
          ...c,
          statements: c.statements.map((s) => ({ ...s, trips: s.trips.map((t) => ({ ...t, items: t.items.filter((i) => !gone(i)) })).filter((t) => t.items.length) })),
        })),
      },
    );
    const what = updated.payments.map((p) => p.vendor || p.name)[0] ?? 'the match';
    setAnnounce(updated.status === 'approved' ? `Approved ${what}. It posts to Xero on the next sync.` : `Rejected ${what}.`);
    setRecent((prev) => byDecision([updated, ...prev.filter((i) => i.id !== updated.id)]));
    refreshSummary();
  };

  const all = useMemo(() => (data?.cards ?? []).flatMap((c) => c.statements.flatMap(itemsOf)), [data]);
  const rules = useMemo(() => [...new Set(all.map((i) => i.rule))], [all]);
  const activeRule = rules.includes(rule as ReviewItem['rule']) ? rule : '';
  const total = useMemo(() => new Set(all.map((i) => i.group ?? i.id)).size, [all]);
  const cards = useMemo(() => {
    const keep = (i: ReviewItem) => !activeRule || i.rule === activeRule;
    return (data?.cards ?? []).map((c) => ({
      ...c,
      statements: c.statements.map((s) => ({ ...s, trips: s.trips.map((t) => ({ ...t, items: t.items.filter(keep) })).filter((t) => t.items.length) })),
    }));
  }, [data, activeRule]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Review</h1>
          <p className="lede">
            {data ? `${plural(total, 'match', 'matches')} waiting, by card. ` : ''}Closed statements first: their payment is due. Approved matches post to Xero on the next sync.
          </p>
        </div>
        {rules.length > 1 && (
          <div role="group" aria-label="Filter by rule" className="pills">
            {['', ...rules].map((r) => (
              <button key={r || 'all'} type="button" className="pill" aria-pressed={activeRule === r} onClick={() => setRule(r)}>
                {r ? (RULE_LABELS[r] ?? r) : 'All'}
              </button>
            ))}
          </div>
        )}
      </header>

      <p className="visually-hidden" role="status">
        {announce}
      </p>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {!data && !error && <Loading kind="cards" />}
      {data && cards.length === 0 && <Empty>Nothing waiting for review.</Empty>}
      {cards.map((c) => (
        <CardSection key={c.accountId ?? c.label} api={api} card={c} isAdmin={isAdmin} onDecided={decided} />
      ))}

      {recent.length > 0 && (
        <section className="recent" aria-labelledby="recent-h">
          <h2 id="recent-h">Recently decided</h2>
          <ul className="card recent-list">
            {recent.map((i) => (
              <li key={i.id}>
                <span className="what">
                  <strong>{i.payments[0]?.vendor || i.lines[0]?.description || i.account}</strong>
                  <span className="small muted">{i.account}</span>
                </span>
                <span className="num">{money(sum(i.payments))}</span>
                <span className={`small decision ${i.status === 'rejected' ? 'rejected' : 'approved'}`}>{decisionText(i)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function CardSection({ api, card, isAdmin, onDecided }: { api: Api; card: CardGroup; isAdmin: boolean; onDecided: (i: ReviewItem) => void }) {
  const headId = useId();
  const n = waitingOn(card);
  return (
    <section className="card-review" aria-labelledby={headId}>
      <header className="card-review-head">
        <div>
          <h2 id={headId}>{card.label}</h2>
          <span className="small muted">{[card.institution, n ? `${plural(n, 'item')} to review` : 'Nothing waiting'].filter(Boolean).join(' · ')}</span>
        </div>
        {card.needsClosingDay && (
          <span className="chip warn">
            {card.suggestedClosingDay ? `Closing day: suggested ${ordinal(card.suggestedClosingDay)}` : 'Closing day needed'}
            {isAdmin && (
              <>
                {' · '}
                <a href="#/connections">Set it</a>
              </>
            )}
          </span>
        )}
      </header>
      {card.statements.map((s) => (
        <StatementSection key={s.closesOn ?? s.state} api={api} card={card} statement={s} onDecided={onDecided} />
      ))}
    </section>
  );
}

function statementTitle(s: StatementGroup): string {
  if (s.state === 'unsplit') return 'All waiting items';
  if (s.state === 'current') return `Current cycle · closes ${day(s.closesOn!)}`;
  return `Closed statement · closed ${day(s.closesOn!)}${s.dueOn ? ` · due ${day(s.dueOn)}` : ''}`;
}

function StatementSection({ api, card, statement: s, onDecided }: { api: Api; card: CardGroup; statement: StatementGroup; onDecided: (i: ReviewItem) => void }) {
  const counts = s.counts;
  const open = counts ? counts.inReview + counts.unmatched : 0;
  return (
    <div className={`statement ${s.state}`}>
      <div className="statement-head">
        <div>
          <h3>{statementTitle(s)}</h3>
          {counts && (
            <span className="small muted">
              {plural(counts.lines, 'charge')} · {counts.matched} matched
              {counts.inReview ? ` · ${counts.inReview} in review` : ''}
              {counts.unmatched ? ` · ${counts.unmatched} unmatched` : ''}
            </span>
          )}
        </div>
        <div className="statement-side">
          {s.total !== undefined && <span className="num statement-total">{money(s.total)}</span>}
          {s.state === 'closed' && counts && <span className={`small ${open ? 'st-warn' : 'st-ok'}`}>{open ? 'Not reconciled yet' : 'All reconciled'}</span>}
          {card.accountId && s.closesOn && (
            <a className="small" href={`#/review/statement/${encodeURIComponent(card.accountId)}/${s.closesOn}`}>
              View statement
            </a>
          )}
        </div>
      </div>
      {s.trips.length === 0 && s.state !== 'unsplit' && <p className="small muted statement-empty">Nothing waiting for review on this statement.</p>}
      {s.trips.map((t) => (
        <TripSection key={t.key || 'none'} api={api} trip={t} onDecided={onDecided} />
      ))}
    </div>
  );
}

function tripDates(t: TripInfo | null): string {
  if (!t?.start) return '';
  return t.end && t.end !== t.start ? `${day(t.start)} – ${day(t.end)}` : day(t.start);
}

function TripSection({ api, trip, onDecided }: { api: Api; trip: TripGroup; onDecided: (i: ReviewItem) => void }) {
  const info = trip.trip;
  // Held-check alternatives stay together.
  const groups: ReviewItem[][] = [];
  for (const i of trip.items) {
    const last = groups.at(-1);
    if (last && i.group && last[0].group === i.group) last.push(i);
    else groups.push([i]);
  }
  return (
    <div className="trip">
      <div className="trip-head">
        <strong>{info?.name ?? trip.name}</strong>
        <span className="trip-facts">
          {tripDates(info) && <span className="chip">{tripDates(info)}</span>}
          <span className="small">Invoiced {info?.invoiced != null ? money(info.invoiced) : '—'}</span>
          <span className="small">Paid {info?.paid != null ? money(info.paid) : '—'}</span>
        </span>
      </div>
      {groups.map((group) =>
        group.length > 1 ? (
          <section className="held" key={group[0].group} aria-label={`Held check, ${group.length} possible payments`}>
            <div className="held-head">
              <strong>Held check, {group.length} possible payments</strong>
              <span className="small muted">Approve the right one; the others close.</span>
            </div>
            {group.map((item) => (
              <ItemCard key={item.id} api={api} item={item} onDecided={onDecided} />
            ))}
          </section>
        ) : (
          <ItemCard key={group[0].id} api={api} item={group[0]} onDecided={onDecided} />
        ),
      )}
    </div>
  );
}

const STATUS_TEXT: Record<StatementDetail['lines'][number]['status'], string> = {
  matched: 'Matched',
  review: 'In review',
  unmatched: 'Unmatched',
  payment: 'Card payment',
};

function StatementView({ api, accountId, closesOn }: { api: Api; accountId: string; closesOn: string }) {
  const [data, setData] = useState<StatementDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    setError(null);
    setData(null);
    api.statement(accountId, closesOn).then(setData, (e: Error) => setError(e.message));
  }, [api, accountId, closesOn]);
  useEffect(load, [load]);
  const trips = useMemo(() => new Map((data?.trips ?? []).map((t) => [t.tripId, t])), [data]);

  return (
    <div className="page">
      <p>
        <a href="#/review">← Back to Review</a>
      </p>
      {error && <ErrorBanner message={error} onRetry={load} />}
      {!data && !error && <Loading kind="table" />}
      {data && (
        <>
          <header className="page-head">
            <div>
              <h1>{data.label}</h1>
              <p className="lede">
                Statement {day(data.opensOn)} – {day(data.closesOn)}
                {data.dueOn ? ` · payment due ${day(data.dueOn)}` : ''}
                {data.institution ? ` · ${data.institution}` : ''}
              </p>
            </div>
            <span className={`chip ${data.reconciled ? '' : 'warn'}`}>{data.reconciled ? 'All reconciled' : 'Not reconciled yet'}</span>
          </header>
          <div className="card statement-summary">
            <div>
              <span className="eyebrow">Statement total</span>
              <span className="num big">{money(data.total)}</span>
            </div>
            <div>
              <span className="eyebrow">Matched</span>
              <span className="num big">{data.counts.matched}</span>
            </div>
            <div>
              <span className="eyebrow">In review</span>
              <span className="num big">{data.counts.inReview}</span>
            </div>
            <div>
              <span className="eyebrow">Unmatched</span>
              <span className="num big">{data.counts.unmatched}</span>
            </div>
          </div>
          {data.lines.length === 0 ? (
            <Empty>No lines on this statement.</Empty>
          ) : (
            <div className="table-card card">
              <table className="table min-640 statement-lines">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Charge</th>
                    <th scope="col">Trip</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="num">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l) => {
                    const trip = l.tripId ? trips.get(l.tripId) : undefined;
                    return (
                      <tr key={l.id} className={`line-${l.status}`}>
                        <td className="mono">{day(l.date)}</td>
                        <td>
                          {l.description}
                          {l.vendor && <span className="small muted block">{[l.vendor, l.payment].filter(Boolean).join(' · ')}</span>}
                        </td>
                        <td>
                          {trip?.name ?? l.trip ?? <span className="muted">—</span>}
                          {trip && tripDates(trip) && <span className="small muted block">{tripDates(trip)}</span>}
                        </td>
                        <td>
                          <span className={l.status === 'matched' ? 'st-ok' : l.status === 'payment' ? 'st-off' : 'st-warn'}>{STATUS_TEXT[l.status]}</span>
                        </td>
                        <td className="num">{money(l.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const RECENT = 10;

/** Person-decided matches, newest first. */
function byDecision(items: ReviewItem[]): ReviewItem[] {
  return items
    .filter((i) => i.decidedBy && i.decidedAt)
    .sort((a, b) => (b.decidedAt ?? '').localeCompare(a.decidedAt ?? ''))
    .slice(0, RECENT);
}

/** "Approved by jitpal · Oct 6, 9:41 AM", with "posted" once it's in Xero. */
function decisionText(i: ReviewItem): string {
  const verb = i.status === 'rejected' ? 'Rejected' : 'Approved';
  const tail = i.status === 'posted' ? ' · posted' : i.status === 'approved' ? ' · posts on the next sync' : '';
  return `${verb} by ${person(i.decidedBy ?? '')} · ${when(i.decidedAt)}${tail}`;
}

function ItemCard({ api, item, onDecided }: { api: Api; item: ReviewItem; onDecided: (i: ReviewItem) => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const noteId = useId();

  const act = async (approve: boolean) => {
    setBusy(true);
    setError(null);
    try {
      onDecided(await (approve ? api.approve(item.id, note) : api.reject(item.id, note)));
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const title = item.payments[0]?.vendor || item.lines[0]?.description || item.account;
  return (
    <article className="card match" aria-labelledby={titleId}>
      <div className="match-head">
        <div>
          <span className="rule-tag">{RULE_LABELS[item.rule] ?? item.rule}</span>
          <h2 className="match-title" id={titleId}>
            {title}
          </h2>
        </div>
        <span className="chip account-chip">{item.account}</span>
      </div>
      <p className="match-reason">{item.reason}</p>
      <div className="sides">
        <div className="side-col">
          <h3 className="eyebrow">Bank and card · {item.account}</h3>
          <ul className="ledger">
            {item.lines.map((l) => (
              <li key={l.id}>
                <span className="date">{day(l.date)}</span>
                <span className="what">{l.description}</span>
                <span className="num">{money(l.amount)}</span>
              </li>
            ))}
            <li className="total">
              <span>{lineCount(item)}</span>
              <span className="num">{money(sum(item.lines))}</span>
            </li>
          </ul>
        </div>
        <div className="side-col">
          <h3 className="eyebrow">Salesforce</h3>
          <ul className="ledger">
            {item.payments.map((p) => (
              <li key={p.id}>
                <span className="date">{day(p.date)}</span>
                <span className="what">
                  <span>{p.vendor}</span>
                  <span className="small muted">{[p.trip || 'No trip', p.method].filter(Boolean).join(' · ')}</span>
                </span>
                <span className="num">{money(p.amount)}</span>
              </li>
            ))}
            <li className="total">
              <span>{item.payments.map((p) => p.name).join(', ')}</span>
              <span className="num">{money(sum(item.payments))}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="match-actions">
        <label className="field" htmlFor={noteId}>
          Note (optional)
          <input id={noteId} type="text" placeholder="Why you approved or rejected this" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </label>
        <div className="buttons">
          <button type="button" className="btn" disabled={busy} onClick={() => act(false)}>
            Reject
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(true)}>
            Approve
          </button>
        </div>
      </div>
      {error && (
        <p className="match-error error-text" role="alert">
          {error}
        </p>
      )}
    </article>
  );
}
